/**
 * Pagina tecnica di riconciliazione di un'operazione (F-096).
 *
 * Risponde a una domanda sola, che prima non aveva risposta in nessuna
 * schermata: **perché questa operazione è avvenuta, e cosa l'ha decisa.**
 *
 * Due scelte che la distinguono dalle altre pagine:
 *
 *  1. **Gli anelli mancanti si mostrano.** Un passo che non è avvenuto è
 *     informazione — dice dove la catena si è interrotta — ed è spesso la
 *     ragione per cui si apre questa pagina. Nasconderlo lascerebbe un diagramma
 *     più pulito e una domanda senza risposta.
 *  2. **Nessun arrotondamento.** È una vista tecnica: gli importi si mostrano
 *     come il backend li serializza, perché il confronto con la ricevuta del
 *     broker si fa cifra per cifra.
 */

import { Link, useParams } from 'react-router-dom';
import { ApiError } from '@/lib/api-client';
import { formatDateTime } from '@/lib/format';
import { Alert, Badge, Card, Spinner } from '@/ui';
import { TraceFlow } from './TraceFlow';
import { STATUS_TONE } from './trace-format';
import { useProposalTrace } from './api';
import './auto-trading.css';

const EMPTY = '—';

function Field({ label, value }: { label: string; value: string | null }) {
  return (
    <div className="trace__field">
      <dt>{label}</dt>
      <dd>{value ?? EMPTY}</dd>
    </div>
  );
}

export function ProposalTracePage() {
  const { id } = useParams<{ id: string }>();
  const query = useProposalTrace(id ?? null);

  if (query.isPending) return <Spinner label="Ricostruzione della catena…" />;

  if (query.isError) {
    const notFound = query.error instanceof ApiError && query.error.status === 404;
    return (
      <Alert variant="error" title={notFound ? 'Operazione non trovata' : 'Errore'}>
        {notFound
          ? 'Questa operazione non esiste, oppure non è tua.'
          : query.error instanceof Error
            ? query.error.message
            : 'Errore imprevisto'}
      </Alert>
    );
  }

  const trace = query.data;

  return (
    <div className="trace">
      <header className="page-header">
        <div>
          <h1 className="trace__title">
            {trace.side === 'buy' ? 'Acquisto' : 'Vendita'} {trace.symbol || trace.instrumentName}
            {/* La scheda del titolo a un clic: da qui si guarda un'operazione,
                e la domanda che segue e' quasi sempre «ma questo titolo cos'e'?».
                Nel titolo e non fra i campi, perche' e' una navigazione, non un
                dato dell'operazione. */}
            <Link
              to={`/instruments/${trace.instrumentId}`}
              className="trace__title-link"
              title={`Apri la scheda di ${trace.instrumentName}`}
              aria-label={`Apri la scheda di ${trace.instrumentName}`}
            >
              ↗
            </Link>
          </h1>
          <p className="page-header__meta">
            {/* F-171: lo stesso codice del registro, per riconciliare a
                colpo d'occhio: «questa e' Brave-Otter-Mango12». */}
            {trace.code && (
              <>
                <code className="auto-trading__code auto-trading__proposal-code" title="Codice della proposta">
                  {trace.code}
                </code>{' '}
                ·{' '}
              </>
            )}
            Run del {trace.runDate} · portafoglio{' '}
            <Link to={`/portfolios/${trace.portfolioId}`}>{trace.portfolioName}</Link>
            {/* L'innesco anche in testata, oltre che come primo anello: e' la
                domanda con cui si arriva qui, e farla scorrere fino al
                diagramma la rende una scoperta invece di una risposta. */}
            {trace.trigger && <> · da {trace.trigger.detail ?? trace.trigger.label}</>}
          </p>
        </div>
        <Badge tone={STATUS_TONE[trace.status] ?? 'neutral'}>{trace.status}</Badge>
      </header>

      {trace.rejectionReasonCode && (
        <Alert variant="warning" title="Operazione non eseguita">
          <strong>{trace.rejectionReasonCode}</strong>
          {trace.rejectionDetail ? ` — ${trace.rejectionDetail}` : ''}
        </Alert>
      )}

      {trace.deferredUntil && (
        <Alert variant="info" title="Rinviata">
          {trace.deferReason ?? 'In attesa'} · riprende il {formatDateTime(trace.deferredUntil)}
        </Alert>
      )}

      <Card title="Da dove viene" subtitle="La catena in ordine cronologico. I passi non avvenuti restano visibili: dicono dove si è interrotta.">
        <TraceFlow steps={trace.steps} />
      </Card>

      <Card title="Numeri" subtitle="Non arrotondati: è la vista con cui si confronta la ricevuta del broker.">
        <dl className="trace__fields">
          <Field label="Importo proposto" value={trace.proposedAmount} />
          <Field label="Importo approvato" value={trace.approvedAmount} />
          <Field label="Importo eseguito" value={trace.executedAmount} />
          <Field label="Quantità" value={trace.quantity} />
          <Field label="Prezzo di riferimento" value={trace.referencePrice} />
          <Field label="Proposta creata" value={formatDateTime(trace.createdAt)} />
        </dl>
      </Card>

      <Card title="Verifica del modello" subtitle="Il passaggio che decide se una proposta diventa un ordine.">
        {trace.llmVerdict ? (
          <>
            <dl className="trace__fields">
              <Field label="Verdetto" value={trace.llmVerdict} />
              <Field label="Confidenza" value={trace.llmConfidence} />
              <Field label="Modello" value={trace.llmModel} />
              <Field label="Token" value={trace.llmTokens === null ? null : String(trace.llmTokens)} />
              {/* Solo quando ce n'e' stato piu' di uno: nel caso normale «1
                  tentativo» sarebbe rumore, mentre «3» dice che il verdetto
                  che si sta leggendo arriva dopo due chiamate non tornate. */}
              {trace.llmCheckAttempts > 1 && (
                <Field label="Tentativi di verifica" value={String(trace.llmCheckAttempts)} />
              )}
            </dl>
            {trace.llmReason && <p className="trace__reason">{trace.llmReason}</p>}
            {trace.llmRiskFlags.length > 0 && (
              <p className="trace__flags">
                {trace.llmRiskFlags.map((flag) => (
                  <Badge key={flag} tone="neutral">
                    {flag}
                  </Badge>
                ))}
              </p>
            )}
          </>
        ) : (
          <p className="cell--minor">
            Non eseguita: la proposta si è fermata prima di arrivare alla verifica.
          </p>
        )}
      </Card>
    </div>
  );
}
